import cn from "classnames";
import { Button } from "components/UI/Buttons/Button";
import Link from "next/link";
import s from "./ArticleCard.module.scss";
import { ArticleDate } from "./ArticleDate";
import Image from "next/image";
import cs from "@/styles/modules/card.module.scss";
import { SITE_NAME } from "@/const";

type Taxonomy = {
  heading: string;
  slug: string;
  category?: {
    heading: string;
    slug: string;
  };
};

type TaxBadgeProps = {
  taxonomy: Taxonomy;
  showSubcategory?: boolean;
  urlPrefix?: string;
};

const TaxBadge = ({ taxonomy, urlPrefix }: TaxBadgeProps) => {
  const heading = taxonomy.heading;
  const url = `${urlPrefix}/${taxonomy.slug}`;

  return (
    <Link key={url} href={url} prefetch={false} className={s.taxBadge}>
      {heading}
    </Link>
  );
};

type ArticleCardProps = {
  heading: string;
  thumbnail: {
    url: string;
    alt: string;
  };
  updatedAt?: string;
  publishedAt: string;
  slug: string;
  author?: {
    url: string;
    name: string;
    avatar: {
      url: string;
      alt: string;
    };
  };
  taxonomy?: Taxonomy | null;
  className?: string;
  showSubcategory?: boolean;
  urlPrefix?: string;
  likes?: number;
  readingTime?: number;
  includeTaxonomyInURL?: boolean;
};

export const ArticleCard = ({
  heading,
  publishedAt,
  updatedAt,
  slug,
  author,
  taxonomy,
  className,
  urlPrefix = "",
  likes,
  readingTime,
  includeTaxonomyInURL = true,
}: ArticleCardProps) => {
  const articleLink = `${urlPrefix}/${includeTaxonomyInURL ? taxonomy?.slug + "/" : ""}${slug}`;

  const authorUrl = author ? `/authors/${author.url}` : "/";

  return (
    <div
      className={cn(
        cs.cardGlassHover,
        s.wrapper,
        {
          [s.noAuthor]: !author,
        },
        className
      )}
    >
      <div className={s.top}>
        <Link href={authorUrl} className={s.author} prefetch={false} suppressHydrationWarning>
          <Image
            src={
              author
                ? author.avatar?.url || "/img/icons/shared/logo-min.svg"
                : "/img/icons/shared/logo-min.svg"
            }
            alt=""
            width={40}
            height={40}
            className={s.authorPhoto}
          />
          <span className={s.authorName}>{author ? author.name : SITE_NAME}</span>
        </Link>

        <div className={s.topLeft}>
          <div className={s.date} suppressHydrationWarning>
            <ArticleDate date={updatedAt || publishedAt} />
          </div>
          {!!likes && (
            <div className={s.likes}>
              <Image src="/img/misc/blue-heart.png" width={16} height={16} alt="" quality={100} />
              <span>{likes} likes</span>
            </div>
          )}
        </div>
      </div>

      <div className={s.center}>
        {taxonomy && (
          <div className={s.taxonomies}>
            <TaxBadge taxonomy={taxonomy} urlPrefix={urlPrefix} />
          </div>
        )}
        {readingTime && (
          <span className={s.readingTime}>
            <Image src="/img/misc/hourglass-sm.png" width={16} height={16} alt="" quality={100} />
            {readingTime} min
          </span>
        )}
      </div>

      <Link href={articleLink} className={s.heading} prefetch={false}>
        {heading}
      </Link>

      <div className={s.bottom}>
        <Button type="white" outline href={articleLink} size="fullwidth">
          Read article
        </Button>
      </div>
    </div>
  );
};
